/**
 * Client for the Telegram bot integration (`/telegram`). Configure the bot
 * token + which events fire, manage recipient chats, and send a test message.
 */
import type {
  TelegramBotIdentity, TelegramChatDiscovery, TelegramConfig, TelegramConfigUpdate, TelegramRecipient,
  TelegramRecipientCreate, TelegramRecipientUpdate, TelegramTestResult,
} from '@erp/shared';

import { api } from './api.js';

export type {
  TelegramBotIdentity, TelegramChatDiscovery, TelegramConfig, TelegramDetectedChat, TelegramRecipient,
} from '@erp/shared';
export { TELEGRAM_EVENT_TYPES } from '@erp/shared';

export async function getTelegramConfig(): Promise<TelegramConfig> {
  return (await api().get<TelegramConfig>('/telegram/config')).data;
}

export async function updateTelegramConfig(input: TelegramConfigUpdate): Promise<TelegramConfig> {
  return (await api().patch<TelegramConfig>('/telegram/config', input)).data;
}

export async function listTelegramRecipients(): Promise<TelegramRecipient[]> {
  return (await api().get<{ items: TelegramRecipient[] }>('/telegram/recipients')).data.items;
}

export async function addTelegramRecipient(input: TelegramRecipientCreate): Promise<TelegramRecipient> {
  return (await api().post<TelegramRecipient>('/telegram/recipients', input)).data;
}

export async function updateTelegramRecipient(id: string, input: TelegramRecipientUpdate): Promise<TelegramRecipient> {
  return (await api().patch<TelegramRecipient>(`/telegram/recipients/${id}`, input)).data;
}

export async function deleteTelegramRecipient(id: string): Promise<void> {
  await api().delete(`/telegram/recipients/${id}`);
}

export async function sendTelegramTest(chatId?: string): Promise<TelegramTestResult> {
  return (await api().post<TelegramTestResult>('/telegram/test', chatId ? { chatId } : {})).data;
}

/**
 * Which bot the stored token belongs to. Always resolves — the failure modes
 * (no token, token rejected, Telegram unreachable) arrive as `status`.
 */
export async function getTelegramBot(refresh = false): Promise<TelegramBotIdentity> {
  return (await api().get<TelegramBotIdentity>('/telegram/bot', refresh ? { params: { refresh: 1 } } : undefined)).data;
}

/** Chats the bot can currently see, so a recipient can be added by clicking. */
export async function detectTelegramChats(): Promise<TelegramChatDiscovery> {
  return (await api().get<TelegramChatDiscovery>('/telegram/updates')).data;
}

// ---------------------------------------------------------------- assistant
export interface TelegramLink {
  id: string;
  userId: string;
  userName: string | null;
  telegramLabel: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

/**
 * Mint a one-time code for the SIGNED-IN user to send the bot as `/link CODE`.
 * Deliberately not admin-gated: it binds a Telegram account to the caller's own
 * identity and grants exactly their own permissions, nothing more.
 */
export async function createTelegramLinkCode(): Promise<{ code: string; expiresAt: string }> {
  return (await api().post<{ code: string; expiresAt: string }>('/telegram/link-code', {})).data;
}

export async function listTelegramLinks(): Promise<TelegramLink[]> {
  return (await api().get<{ items: TelegramLink[] }>('/telegram/links')).data.items;
}

/** Revoke a link — that phone stops getting answers immediately. */
export async function deleteTelegramLink(id: string): Promise<void> {
  await api().delete(`/telegram/links/${id}`);
}
